import { Calendar } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

/**
 * The usage strip on `/subscribe`: how much the user has actually cooked, stated as a fact.
 *
 * **Never coloured, never a verdict.** This is the component most at risk of drifting into a
 * recommendation — a green tick, an amber warning, "you'd save $3 on Pinch" — and it must not.
 * Parsley shows the number; the user draws the conclusion. It sits on the neutral `card-sunken`
 * surface for exactly that reason, with no tone variants to reach for.
 *
 * The zero-usage case is a real and likely state, not an edge case: a beta user who never planned
 * a dinner still has to choose a plan. "You planned 0 dinners" would be a poor answer, so the copy
 * says there's nothing to weigh and points back at the plan descriptions.
 *
 * Presentational: `line` and `sub` arrive pre-formatted. No counting, no date math.
 */
export interface UsagePanelProps {
  state: 'has-usage' | 'zero-usage' | 'loading'
  /** Small caps heading, e.g. "Your last 30 days". */
  kicker?: string
  /** The fact. `ReactNode` so the app can bold the numeral without this parsing markup. */
  line?: React.ReactNode
  /** Provenance or explanation, e.g. "Counted from your meal plans, March 4 – April 3." */
  sub?: string
  /** `aria-label` for the loading state. */
  loadingLabel?: string
  className?: string
}

const SHELL =
  'flex items-center gap-[15px] rounded-2xl bg-card-sunken px-[19px] py-[15px] max-[560px]:gap-3 max-[560px]:px-[15px] max-[560px]:py-[13px]'
const TILE =
  'flex size-[38px] shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_oklab,oklch(var(--foreground))_7%,transparent)] text-muted-foreground'
const SKELETON = 'animate-pulse rounded-md bg-[color-mix(in_oklab,oklch(var(--foreground))_9%,transparent)]'

export const UsagePanel = React.forwardRef<HTMLDivElement, UsagePanelProps>(
  ({ state, kicker, line, sub, loadingLabel = 'Loading your usage', className }, ref) => {
    if (state === 'loading') {
      return (
        // biome-ignore lint/a11y/useSemanticElements: a polite live region for a loading state; <output> is a form-result element with the wrong semantics. The role is also what makes aria-label valid — a bare div supports neither.
        <div ref={ref} role="status" aria-busy="true" aria-label={loadingLabel} className={cn(SHELL, className)}>
          <div className={TILE}>
            <div className={cn(SKELETON, 'size-[18px]')} />
          </div>
          <div className="min-w-0 flex-1">
            <div className={cn(SKELETON, 'h-2.5 w-[110px]')} />
            <div className={cn(SKELETON, 'mt-2 h-3.5 w-[210px]')} />
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn(SHELL, className)}>
        <div className={TILE}>
          <Calendar className="size-[18px]" strokeWidth={1.8} aria-hidden />
        </div>
        <div className="min-w-0">
          {kicker && <div className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-muted-foreground">{kicker}</div>}
          {/* DM Mono numerals inside <b>, so a count lines up with the prices on the cards. */}
          <div className="mt-[3px] text-[15px] font-semibold leading-[1.4] text-foreground [&_b]:font-mono [&_b]:font-medium [&_b]:tabular-nums">
            {line}
          </div>
          {sub && <div className="mt-[3px] text-pretty text-[12.5px] leading-[1.5] text-muted-foreground">{sub}</div>}
        </div>
      </div>
    )
  }
)
UsagePanel.displayName = 'UsagePanel'
